import type {DriveStep} from 'driver.js';

export type TourDefinition = {
  name: string;
  steps: DriveStep[];
  // Optional: user roles that can see this tour (if empty, available to all)
  allowedRoles?: string[];
  // Optional: state management for complex tours
  stepStates?: Array<Record<string, any>>;
  // Optional: hide tour on mobile devices
  hiddenOnMobile?: boolean;
};

export type TourConfig = Record<string, TourDefinition>;

export const tours: TourConfig = {
  'dashboard-en': {
    name: 'Dashboard Customization',
    steps: [
      {
        element: '#scrolling',
        popover: {
          title: 'Welcome to Your Workspace!',
          description:
            'This is your space to make your own. Customize your dashboard with the widgets that matter most — your command center for key info and tools.',
          side: 'bottom',
          align: 'start',
        },
      },
      {
        element: '.tour-widget',
        popover: {
          title: 'Dashboard Widgets',
          description:
            'These are your widgets — handy panels that show key info and tools. Each one has a job.',
          side: 'right',
          align: 'start',
        },
      },
      {
        element: '.card-header',
        popover: {
          title: 'Widget Controls',
          description:
            'Each widget has header buttons to interact with it — refresh, settings, expand, and more.',
          side: 'bottom',
          align: 'start',
        },
      },
      {
        element: '[id="cores.dashboard.widgettoolbar.editdashboard"]',
        popover: {
          title: 'Edit Your Dashboard',
          description:
            "Click this to edit your dashboard — add, remove, and rearrange widgets.\n\nLet's try it now!",
          side: 'bottom',
          align: 'center',
        },
      },
      {
        element: '#scrolling',
        popover: {
          title: 'Customizing Your Dashboard',
          description:
            'In edit mode, drag to move widgets and drag corners to resize. Make it yours.',
          side: 'bottom',
          align: 'start',
        },
      },
      {
        element: '[id="cores.dashboard.widgettoolbar.done"]',
        popover: {
          title: 'Save Your Changes',
          description: "When you're happy, click this to save your layout.",
          side: 'bottom',
          align: 'center',
        },
      },
    ],
    stepStates: [
      {dashboardEdit: false},
      {dashboardEdit: false},
      {dashboardEdit: false},
      {dashboardEdit: false},
      {dashboardEdit: true},
      {dashboardEdit: true},
    ],
  },

  'dashboard-nl': {
    name: 'Dashboard aanpassen',
    steps: [
      {
        element: '#scrolling',
        popover: {
          title: 'Welkom in je werkruimte!',
          description:
            'Dit is je persoonlijke werkruimte waar je je dashboard kunt aanpassen met widgets die voor jou het belangrijkst zijn.',
          side: 'bottom',
          align: 'start',
        },
      },
      {
        element: '.card',
        popover: {
          title: 'Dashboard-widgets',
          description:
            'Dit zijn je dashboard-widgets — handige tools en weergaven voor je dagelijkse werk.',
          side: 'right',
          align: 'start',
        },
      },
      {
        element: '.card-header',
        popover: {
          title: 'Widgetbediening',
          description:
            'Elke widget heeft knoppen in de header om mee te werken, zoals verversen, instellingen of uitbreiden.',
          side: 'bottom',
          align: 'start',
        },
      },
      {
        element: '[id="cores.dashboard.widgettoolbar.editdashboard"]',
        popover: {
          title: 'Dashboard bewerken',
          description:
            'Klik op deze knop om de bewerkingsmodus te openen en je dashboardindeling aan te passen.',
          side: 'bottom',
          align: 'center',
        },
      },
      {
        element: '#scrolling',
        popover: {
          title: 'Dashboard aanpassen',
          description:
            'In de bewerkingsmodus kun je widgets verplaatsen en groter/kleiner maken zoals je wilt.',
          side: 'bottom',
          align: 'start',
        },
      },
      {
        element: '[id="cores.dashboard.widgettoolbar.done"]',
        popover: {
          title: 'Wijzigingen opslaan',
          description: 'Klik hier om je wijzigingen op te slaan en de bewerkingsmodus te verlaten.',
          side: 'bottom',
          align: 'center',
        },
      },
    ],
    stepStates: [
      {dashboardEdit: false},
      {dashboardEdit: false},
      {dashboardEdit: false},
      {dashboardEdit: false},
      {dashboardEdit: true},
      {dashboardEdit: true},
    ],
  },

  'user-invitation-en': {
    name: 'User Invitation Guide',
    allowedRoles: ['invitation_managers', 'Superaccess'],
    hiddenOnMobile: true,
    steps: [
      {
        element: '#settings-menu-btn',
        popover: {
          title: 'Open your profile menu',
          description: 'Click this button to open the profile menu.',
          side: 'bottom',
          align: 'start',
        },
      },
      {
        element: '[id="settings.open"]',
        popover: {
          title: 'Settings Menu',
          description: 'Click here to open settings.',
          side: 'bottom',
          align: 'start',
        },
      },
      {
        element: '#create-user',
        popover: {
          title: 'Go to Create User',
          description: 'Pick this option to open the Create User view.',
          side: 'bottom',
          align: 'start',
        },
      },
      {
        element: '[data-testid="team.create-user-form"]',
        popover: {
          title: 'Invite New Users',
          description: 'Use this view to invite new teammates. Set their access right away.',
          side: 'bottom',
          align: 'start',
        },
      },
      {
        element: '[data-testid="team.create-user-form.emails"]',
        popover: {
          title: 'Type an email address',
          description:
            "Type the email of the person you want to invite. They'll get the invite there.",
          side: 'right',
          align: 'start',
        },
      },
      {
        element: '[data-testid="team.create-user-form.roles"]',
        popover: {
          title: 'Assign Roles & Permissions',
          description: 'Pick the right roles for this user. Roles control access and permissions.',
          side: 'left',
          align: 'start',
        },
      },
      {
        element: '[data-testid="team.create-user-form.groups"]',
        popover: {
          title: 'Assign Groups',
          description:
            'Pick the groups this user should belong to. Groups help manage permissions and access.',
          side: 'left',
          align: 'start',
        },
      },
      {
        element: '[id="team.create-user-form.submit"]',
        popover: {
          title: 'Send Invitation',
          description:
            "Click Send to email the invitation. They'll accept it and set up their account to get access.",
          side: 'top',
          align: 'center',
        },
      },
    ],
    stepStates: [
      {settingsOpen: false, settingsDropdownOpen: false, sidebarHide: true},
      {settingsOpen: false, settingsDropdownOpen: true},
      {settingsOpen: true, settingsTab: 'create-user'},
    ],
  },

  'user-invitation-nl': {
    name: 'Gebruiker uitnodigen',
    allowedRoles: ['invitation_managers', 'Superaccess'],
    hiddenOnMobile: true,
    steps: [
      {
        element: '#settings-menu-btn',
        popover: {
          title: 'Open je profielmenu',
          description: 'Klik op deze knop om het profielmenu te openen.',
          side: 'bottom',
          align: 'start',
        },
      },
      {
        element: '[id="settings.open"]',
        popover: {
          title: 'Instellingenmenu',
          description: 'Klik hier om het instellingenmenu te openen.',
          side: 'bottom',
          align: 'start',
        },
      },
      {
        element: '#create-user',
        popover: {
          title: 'Ga naar Gebruiker aanmaken',
          description: 'Selecteer deze optie om de weergave Gebruiker aanmaken te openen.',
          side: 'bottom',
          align: 'start',
        },
      },
      {
        element: '[data-testid="team.create-user-form"]',
        popover: {
          title: 'Nieuwe gebruikers uitnodigen',
          description:
            'Gebruik deze weergave om teamleden uit te nodigen. U kunt direct de juiste toegangsrechten instellen.',
          side: 'bottom',
          align: 'start',
        },
      },
      {
        element: '[data-testid="team.create-user-form.emails"]',
        popover: {
          title: 'E-mailadres typen',
          description:
            'Typ het e-mailadres van de persoon die je wilt uitnodigen. Die krijgt de uitnodiging per e-mail.',
          side: 'right',
          align: 'start',
        },
      },
      {
        element: '[data-testid="team.create-user-form.roles"]',
        popover: {
          title: 'Rollen en rechten kiezen',
          description:
            'Kies de juiste rollen voor deze gebruiker. Rollen bepalen toegang en rechten.',
          side: 'left',
          align: 'start',
        },
      },
      {
        element: '[data-testid="team.create-user-form.groups"]',
        popover: {
          title: 'Groepen toewijzen',
          description: 'Kies de groepen waar deze gebruiker lid van moet worden.',
          side: 'left',
          align: 'start',
        },
      },
      {
        element: '[id="team.create-user-form.submit"]',
        popover: {
          title: 'Uitnodiging verzenden',
          description:
            'Klik op Verzenden om de uitnodiging te mailen. De gebruiker accepteert en zet een account op om toegang te krijgen.',
          side: 'top',
          align: 'center',
        },
      },
    ],
    stepStates: [
      {settingsOpen: false, settingsDropdownOpen: false, sidebarHide: true},
      {settingsOpen: false, settingsDropdownOpen: true},
      {settingsOpen: true, settingsTab: 'create-user'},
    ],
  },

  'file-manager-en': {
    name: 'File Manager Guide',
    steps: [
      {
        element: '[data-testid="file-manager.table"]',
        popover: {
          title: 'Welcome to File Manager',
          description:
            'This is your file management workspace. Upload, organize, and manage your files and folders here.',
          side: 'bottom',
          align: 'start',
        },
      },
      {
        element: '[data-testid="search"]',
        popover: {
          title: 'Table Search',
          description:
            'Use this search box to quickly find specific files or folders by typing their name. The table will filter results in real-time.',
          side: 'bottom',
          align: 'start',
        },
      },
      {
        element: '[data-testid="file-manager.table"] thead th:nth-child(2)',
        popover: {
          title: 'Column Sorting',
          description:
            'Click on any column header to sort your files by that criteria - name, size, date modified, etc. Click again to reverse the sort order.',
          side: 'bottom',
          align: 'center',
        },
      },
      {
        element: '[name="refresh-button"]',
        popover: {
          title: 'Refresh Files',
          description: 'Click refresh to update the file list and see the latest changes.',
          side: 'bottom',
          align: 'center',
        },
      },
      {
        element: '[data-testid="file-manager.upload.button"]',
        popover: {
          title: 'Upload Files',
          description:
            'Click here to upload files from your computer. You can select multiple files at once.',
          side: 'bottom',
          align: 'center',
        },
      },
      {
        element: '[data-testid="file-manager.createfolder.button"]',
        popover: {
          title: 'Create Folders',
          description: 'Use this button to create new folders for organizing your files.',
          side: 'bottom',
          align: 'center',
        },
      },
      {
        element: '[data-testid="file-manager.copy.button"]',
        popover: {
          title: 'File Operations Toolbar',
          description:
            'Use the checkboxes to select files, then these toolbar buttons become available for operations like copy, cut, paste, delete, rename, and download.',
          side: 'top',
          align: 'center',
        },
      },
    ],
  },

  'file-manager-nl': {
    name: 'Bestandsbeheer Gids',
    steps: [
      {
        element: '[data-testid="file-manager.table"]',
        popover: {
          title: 'Welkom bij Bestandsbeheer',
          description:
            'Dit is je werkruimte voor bestandsbeheer. Upload, organiseer en beheer hier je bestanden en mappen.',
          side: 'bottom',
          align: 'start',
        },
      },
      {
        element: '[data-testid="search"]',
        popover: {
          title: 'Tabel zoekfunctie',
          description:
            'Gebruik dit zoekvak om snel specifieke bestanden of mappen te vinden door hun naam te typen. De tabel filtert resultaten in realtime.',
          side: 'bottom',
          align: 'start',
        },
      },
      {
        element: '[data-testid="file-manager.table"] thead th:nth-child(2)',
        popover: {
          title: 'Kolom sorteren',
          description:
            'Klik op een kolomkop om je bestanden te sorteren op dat criterium - naam, grootte, wijzigingsdatum, etc. Klik opnieuw om de sorteervolgorde om te keren.',
          side: 'bottom',
          align: 'center',
        },
      },
      {
        element: '[name="refresh-button"]',
        popover: {
          title: 'Bestanden vernieuwen',
          description:
            'Klik op vernieuwen om de bestandenlijst bij te werken en de laatste wijzigingen te zien.',
          side: 'bottom',
          align: 'center',
        },
      },
      {
        element: '[data-testid="file-manager.upload.button"]',
        popover: {
          title: 'Bestanden uploaden',
          description:
            'Klik hier om bestanden van je computer te uploaden. Je kunt meerdere bestanden tegelijk selecteren.',
          side: 'bottom',
          align: 'center',
        },
      },
      {
        element: '[data-testid="file-manager.createfolder.button"]',
        popover: {
          title: 'Mappen aanmaken',
          description:
            'Gebruik deze knop om nieuwe mappen te maken voor het organiseren van je bestanden.',
          side: 'bottom',
          align: 'center',
        },
      },
      {
        element: '[data-testid="file-manager.copy.button"]',
        popover: {
          title: 'Bestandsoperaties werkbalk',
          description:
            'Gebruik de selectievakjes om bestanden te selecteren, dan worden deze werkbalk knoppen beschikbaar voor operaties zoals kopiëren, knippen, plakken, verwijderen, hernoemen en downloaden.',
          side: 'top',
          align: 'center',
        },
      },
    ],
  },
};
